/**
 * Every number the payment flow shows or sends comes from here, and every number
 * here comes from rista-library's `calculateForSale` / `calculateForNC`.
 *
 * v1 computed balances in at least four places with slightly different rules —
 * `handleFullPaymentTranscation` (library + a hand-rolled fallback),
 * `handleAfterPaymentCompleted` (`billRoundedAmount - amount`), `prepareSaleData`
 * (`balanceAmount - amount`) and SplitComponentDesktop's refresh handler. That is
 * how a bill ends up disagreeing with itself. There is one calculator now.
 *
 * The library comes from the npm package, not the `window.RL` global script that
 * apps/web loads — same code, but it resolves in node, in a kiosk build and in a
 * worker, and it fails at build time instead of at runtime. The deep import
 * matches apps/mobile (`src/utils/global.js`) and keeps reports/e-invoicing/po
 * out of the bundle.
 */
import saleutil from "@dotpe/rista-library/lib/sale/sale_util.js";
import type { PaymentLeg, SaleContext, TxnType } from "./types";
import { normalizePayments, round2 } from "./legs";
import type { NormalizedLegs } from "./legs";
import { ASYNC_MODES, TXN_TYPE } from "./types";

const LEG_TYPE_COUPON = "Coupon";

/** Return sales settle negative — v1 did this in `getPaymentArrForSaleReturn`. */
function signed(legs: PaymentLeg[], returnSale: boolean): PaymentLeg[] {
  if (!returnSale) return legs;
  return legs.map((leg) => ({ ...leg, amount: leg.amount > 0 ? -leg.amount : leg.amount }));
}

/**
 * A leg whose amount is with a provider rather than with us.
 *
 * `type === 'External'` covers it from the moment the leg exists, which is v1's
 * rule and stays untouched. The other half is a **deliberate divergence**: `Rista
 * Card` and `Prepaid` settle asynchronously without being typed External, and v1
 * knows it — `isCtaActive` (`CheckoutContainer:384`) names all three when deciding
 * whether Save/Confirm may be pressed. But `getNewPaymentInfo` tests only
 * `type === 'External'`, so v1 blocks the button while still COUNTING a requested
 * Rista Card leg as collected: the bill reads paid, the balance reads zero, and
 * only the greyed-out CTA says otherwise.
 *
 * Reachable on any reopened sale — `seedLegs` copies the server's leg with its
 * amount and a `Requested` status intact, never having passed through
 * `markLegRequested`, which is what zeroes the amount on the runner path.
 */
function awaitsProvider(leg: PaymentLeg): boolean {
  /**
   * The two non-External modes that still block a commit while in flight.
   * Was `providerFor(mode).blocksCommitWhileRequested`, a lookup into v2's
   * provider table — v1 names them literally (`isCtaActive:386`:
   * `type === EXTERNAL || mode === RISTA_CARD || mode === PREPAID`), so the
   * money layer needs no registry.
   *
   * TODO(pass 2): v1 tests `paymentStatus === REQUESTED` ALONE. "Pending" was
   * widened in by v2. Kept as-is here because this pass moves shapes, not
   * rules — see §26.
   */
  if (!ASYNC_MODES.includes(leg.mode as any)) return false;
  return leg.paymentStatus === "Requested" || leg.paymentStatus === "Pending";
}

/**
 * A Rista Card leg picked but whose card has not been validated
 * (`bn/ristacard/sale/validate`) — no card is charged yet, so nothing is
 * collected and the amount stays DUE. A **divergence from v1**, which counts the
 * leg the moment it is picked (`getNewPaymentInfo` tests only External) and
 * blocks the CTA only once it is `Requested`, so Confirm closed the sale without
 * any card being picked.
 *
 * Keyed on the VALIDATE RESULT, not on the leg having no status: a reopened
 * sale's debited leg can come back with neither `paymentStatus` nor `posted`
 * (`wireToLegState` seeds it DRAFT), but it always carries the `ristaCardId` the
 * validate wrote (`providers/ristaCard/logic.ts` `buildLeg`).
 */
function awaitsCardValidation(leg: PaymentLeg): boolean {
  return leg.mode === "Rista Card" && !leg.posted && !leg.paymentStatus && !(leg as any).ristaCardId;
}

/** v1's `getNewPaymentInfo` test, plus the modes v1 only remembers in `isCtaActive`. */
const settlesAsync = (leg: PaymentLeg) =>
  leg.type === "External" || awaitsProvider(leg) || awaitsCardValidation(leg);

/**
 * Session legs → what the library and the backend expect. See ./legs.
 *
 * A SPLIT leg the operator has picked but not yet ADDED is skipped: v1 keeps it in
 * `checkoutContextState.selectedPayment`, outside the `payments` Map that
 * `getNewPaymentInfo` reads, so it moves no total and reaches no wire until
 * `handleSplitClick` registers it (`defaultFlow:120`). `legs.ts` stays a verbatim
 * port of that helper; the "is it in the Map yet" question belongs here, and so
 * does `settlesAsync` — the balance the operator reads has to agree with the CTA.
 */
export function normalizeLegs(legs: PaymentLeg[], returnSale = false): NormalizedLegs {
  return normalizePayments(signed(legs.filter((leg) => !leg.draft), returnSale), settlesAsync);
}

/**
 * The one call. `params` is the host app's `getParamsForSaleCalculate(sale)`.
 *
 * Returns whatever the library returns — which is EITHER a calculated sale OR a
 * bare `{ code }` rejection (`ERR_TRANSACTION_TYPE_NOT_SET`,
 * `REST_ERR_DISCOUNT_LIMIT_EXCEEDED`, `REST_INVALID_ORIGINAL_SALE`…). Callers
 * must check; `selectTotals` does.
 */
export function calculateWithLegs(legs: PaymentLeg[], ctx: SaleContext, params: any): any {
  /**
   * ...but only for the rejections it reaches. `calculate` runs
   * `generateItemNumbers` FIRST (`sale_util.js:5167`), which reads
   * `sale.saleItems.filter` unguarded (`sale_util.js:64`), so a sale without
   * `saleItems` THROWS instead of returning `{ code }`.
   *
   * There is always at least one render with no snapshot: `usePaymentFlow` opens
   * the session in an effect (`usePaymentFlow.ts:264`) and effects run after the
   * first render, while `usePaymentSession` computes totals DURING it — and the
   * unmount teardown nulls the snapshot again. Reject it here, the way the
   * library would have if it had got that far.
   */
  if (!ctx.sale?.saleItems) return { code: "ERR_SALE_NOT_CALCULATED" };
  const { paymentsArr } = normalizeLegs(legs, !!ctx.returnSale);
  const draft = structuredClone(ctx.sale ?? {});
  // the library reads `paymentInfo.tip`, NOT `tipAmount`
  draft.paymentInfo = { ...(draft.paymentInfo ?? {}), payments: paymentsArr, tip: (ctx.tip ?? 0) };
  /**
   * ...and the library can also THROW rather than return a `{ code }`, which is the same
   * class of failure as the guard above and was not handled at all.
   *
   * The reachable case: `params.items` is built by the host's `getSaleItems`, which pushes
   * `itemsData[line.itemId]` per cart line WITHOUT filtering
   * (`helpers/saleHelpers/index.desktop.ts:103`, app code kept as-is). A line whose item is
   * not in the catalogue store therefore puts `undefined` in that array, and
   * `calculateForSale` dereferences it unguarded (`sale_util.js:5390`). An operator hits it
   * when a catalogue refresh drops an item that is already in a cart.
   *
   * Folded into the rejection shape the caller already handles, so the bill flags itself
   * (`totals.error`) and Confirm is refused with a reason — instead of taking the whole
   * checkout down mid-render. FLAGGED, not fixed at the source: filtering `getSaleItems`
   * would change what every existing POS caller prices against, which is not this
   * package's call to make.
   */
  try {
    if ((ctx.txnType ?? TXN_TYPE.FULL) === TXN_TYPE.NC) {
      draft.transactionType = TXN_TYPE.NC;
      saleutil.calculateForNC(draft, params);
      return draft;
    }
    return saleutil.calculateForSale(draft, params);
  } catch {
    return { code: "ERR_SALE_NOT_CALCULATED" };
  }
}

export interface PaymentTotals {
  /** what the bill rounds to, per the library */
  billAmount: number;
  /** collected so far (posted legs) */
  collected: number;
  /** in-flight on unposted External legs */
  requested: number;
  /** still due — never negative */
  balance: number;
  /** cash to return when tendered exceeds due */
  change: number;
  /** the calculated sale, so callers don't run the library twice. null when `error` is set. */
  sale: any | null;
  /**
   * The bill, broken out — what the design's "Bill summary" panel shows.
   *
   * These are the library's own fields, chosen so the rows RECONCILE:
   * `billSubTotal + billDiscountTotal + billTaxTotalExcluded + charges` is exactly
   * `billTotal` (`sale_util.js:5264`), and `billRoundedAmount` is that plus
   * `roundOffAmount`. Two traps in picking them:
   *
   *  - `discountTotal` is `billItemDiscountTotal + billDiscountTotal`, and the item
   *    half is ALREADY inside `billSubTotal` (`sale_util.js:684` subtracts it to
   *    recover the pre-discount figure). Showing it would double-count.
   *  - `directChargeTotal` is a separate field from `chargeTotal` but `billTotal`
   *    adds both, so the Charges row is their sum. v1's cart breakdown
   *    (`MoreBillInfo:62`) shows `chargeTotal` alone — which is why its rows do not
   *    add up when a direct charge exists. Not mirrored; flagged instead.
   *
   * `null` when the sale did not calculate, alongside `error`.
   */
  breakdown: {
    subTotal: number;
    /** negative — the library stores discounts as a reduction */
    discount: number;
    charges: number;
    /** tax NOT already inside the item price; the included half needs no row */
    tax: number;
    /** the difference between the rows and `billAmount`, per the rounding mode */
    roundOff: number;
  } | null;
  /**
   * The library rejected the sale. Amounts are zeroed rather than guessed — v1
   * fell back to `billRoundedAmount + tip - paid` here, which quietly ignores the
   * rounding mode and charges and lets a wrong bill reach the backend. A flagged
   * zero can't be committed (`selectCanCommit`), a plausible wrong number can.
   */
  error?: string;
}

export function selectTotals(legs: PaymentLeg[], ctx: SaleContext, params: any): PaymentTotals {
  const { amount, collectedAndRequestedAmount } = normalizeLegs(legs, !!ctx.returnSale);
  const collected = amount;
  const requested = collectedAndRequestedAmount - amount;

  /**
   * Return sales do NOT go through the library. v1 makes that choice explicitly —
   * `handleFullPaymentTranscation` skips `calculateSale` for a return and uses
   * `billRoundedAmount + tip - collected`, and `handleCheckoutAction` skips the
   * recalculation too. Running `calculateForSale` here would produce different
   * numbers on the refund path, which DD3 (byte-identical output while v1 and v2
   * terminals sync over LAN) forbids.
   *
   * Amounts are negative on a return: `createLeg` keeps the negative balance, so
   * `collected` is negative and the arithmetic below matches v1's `fallbackBalance`.
   */
  if (ctx.returnSale) {
    const bill = ctx.sale?.billRoundedAmount ?? 0;
    return {
      billAmount: bill,
      breakdown: null, // a return does not go through the library — see above
      collected,
      requested,
      // signed: negative means still owed to the customer. Clamped at commit only,
      // the way v1 clamps `paymentInfo.balanceAmount`.
      balance: round2(round2(bill + (ctx.tip ?? 0)) - round2(collected)),
      change: 0, // a refund never returns change
      sale: ctx.sale,
    };
  }

  const sale = calculateWithLegs(legs, ctx, params);

  /**
   * `billRoundedAmount` has to be a real number, not merely present.
   * `roundOffBillAmountBasedOnPaymentModes` returns whatever the arithmetic gave it
   * — a NUMERIC STRING on some rounding modes, which is why `roundBill` parses its
   * result, and `NaN` when anything upstream of `sale.billTotal` failed to compute.
   *
   * A NaN reaching the UI is silent: `formatMoney` does `Number(value || 0)` and
   * `NaN || 0` is 0, so the panel shows ₹0.00 with no complaint — and worse,
   * `balance` is then 0 too, `selectCoversBill` passes and the sale can be CLOSED
   * having collected nothing. That is precisely the case `totals.error` exists for
   * ("a flagged zero can't be committed, a plausible wrong number can"), so it is
   * flagged rather than displayed.
   *
   * `Number.isFinite(Number(…))`, not a null check: it has to accept `0` (a fully
   * discounted bill) and `"245.60"` while rejecting undefined, null and NaN.
   */
  const billOk = !!sale?.paymentInfo && Number.isFinite(Number(sale.billRoundedAmount));

  if (!sale || sale.code || !billOk) {
    return {
      billAmount: 0,
      breakdown: null,
      collected,
      requested,
      balance: 0,
      change: 0,
      sale: null,
      error: sale?.code ?? (sale?.paymentInfo ? "ERR_BILL_NOT_CALCULATED" : "ERR_SALE_NOT_CALCULATED"),
    };
  }

  const rawBalance = sale.paymentInfo.balanceAmount ?? 0;
  return {
    billAmount: sale.billRoundedAmount ?? 0,
    breakdown: {
      subTotal: sale.billSubTotal ?? 0,
      discount: sale.billDiscountTotal ?? 0,
      charges: round2((sale.chargeTotal ?? 0) + (sale.directChargeTotal ?? 0)),
      tax: sale.billTaxTotalExcluded ?? 0,
      roundOff: sale.roundOffAmount ?? 0,
    },
    collected,
    requested,
    balance: rawBalance > 0 ? rawBalance : 0,
    change: rawBalance < 0 ? -rawBalance : 0,
    sale,
  };
}

/**
 * A refund cannot exceed what the original sale still owes. v1 checks this at the
 * top of `handleCheckoutAction` and pops RETURN_PAYMENT_ERROR; here it is a
 * selector so the CTA can be disabled instead of failing on press.
 */
export function selectOverRefund(legs: PaymentLeg[], ctx: SaleContext): boolean {
  if (!ctx.returnSale) return false;
  const owed = ctx.sale?.paymentInfo?.balanceAmount ?? 0;
  const { amount } = normalizeLegs(legs, true);
  return Math.abs(amount) > Math.abs(owed);
}

/**
 * What THIS leg may be tendered up to. The one copy of the rule — the pricing
 * cap, the ADD PAYMENT gate, the pre-hand-off gate and the cash strip all ask it.
 *
 * The add-back is what the balance actually COUNTED for this leg, which is not
 * the same as its amount: a SPLIT draft is skipped by `normalizeLegs`, and an
 * unposted async leg is zeroed by `normalizePayments` — adding either back lets
 * the operator type twice what is owed. v1 needs neither case: its balance is a
 * stored field only `handleSplitClick` decrements.
 *
 * Two things the arithmetic must not lose:
 *  - UNCLAMPED. `totals.balance` floors at zero and an over-tender's surplus sits
 *    in `change`, so 900 cash on a 500 bill reads `{ balance: 0, change: 400 }`.
 *  - SIGNED. On a return both are negative: `|-300 + -200|` is the 500 still
 *    refundable, `|-300| + 200` is 100.
 */
function countedInBalance(leg?: PaymentLeg): number {
  if (!leg || leg.draft) return 0;
  // `normalizePayments` zeroes an async leg until the provider posts it
  if (settlesAsync(leg) && !leg.posted) return 0;
  return leg.amount || 0;
}

export function availableForLeg(totals: PaymentTotals, leg?: PaymentLeg): number {
  const unclamped = (totals.balance ?? 0) - (totals.change ?? 0);
  const room = unclamped + countedInBalance(leg);
  // room pointing the other way is over-collection, not headroom. The BILL's
  // sign is the direction; only `returnSale` makes it negative, and a bill of
  // zero has no direction of its own, so it takes a sale's.
  const direction = Math.sign(totals.billAmount ?? 0) || 1;
  return Math.sign(room) === direction ? round2(Math.abs(room)) : 0;
}

/**
 * v1's `checkValidAmount` (`paymenthelper:424`): the tendered total must cover the
 * bill — but with a tolerance of ONE unit of currency.
 *
 *   saleData.paymentInfo.balanceAmount - enteredAmount >= 1  ->  refused
 *
 * So v1 lets a sale close up to 99 paise short without complaint, and that slack is
 * deliberate enough to mirror rather than tighten: dropping it would start refusing
 * closes that v1 accepts, on rounding alone.
 */
export function selectCoversBill(totals: PaymentTotals): boolean {
  return totals.balance < 1;
}

/**
 * v1's `validatePayments` (`paymenthelper:689`), which runs on the SELECTED leg
 * only — not on every leg. Two rules survive into v2:
 *
 *  - a reference is required when the payment def says so. NOTHING in v2 checked
 *    this before; `referenceMandatory` was carried on the leg and never read.
 *  - `validateAmount`: non-cash may not exceed the balance. `capNonCashAmount`
 *    already enforces this at input time (as v1 does in `TouchInput:144`, which is
 *    type-independent), so this is the belt to that braces.
 */
export function selectActiveLegValid(
  legs: PaymentLeg[],
  activeLegId: string | null,
  totals: PaymentTotals
): boolean {
  const leg = legs.find((candidate) => candidate.paymentId === activeLegId);
  // v1 passes `{}` when nothing is selected, and validateAmount waves that through
  if (!leg || !leg.amount) return true;
  if (leg.referenceMandatory && !leg.reference) return false;
  if (!leg.isCash && !leg.posted) {
    if (Math.abs(leg.amount) > availableForLeg(totals, leg)) return false;
  }
  return true;
}

/**
 * Replacement for `isCtaActive`. v1 listed the blocking modes inline
 * (`External || RISTA_CARD || PREPAID`); here the provider table says so.
 *
 * Pass `totals` so a sale the library rejected can never be committed, and
 * `opts.allowOpenBalance` (the host's `CLOSE_SALE_BALANCE` permission) to say
 * whether closing with money still owed is permitted.
 *
 * The `legs.length === 0` case did not exist in v1: the auto-select guaranteed a
 * leg whenever the balance was positive, so "Confirm with nothing tendered" was
 * unreachable. Removing the default selection (§7.7) makes it reachable, so it is
 * refused here rather than left to the CLOSE_SALE_BALANCE check further down the
 * commit path.
 */
export function selectCanCommit(
  legs: PaymentLeg[],
  ctx: SaleContext,
  totals?: PaymentTotals,
  opts?: { allowOpenBalance?: boolean; settling?: boolean; activeLegId?: string | null }
): boolean {
  if (totals?.error) return false;
  if (opts?.settling) return false;
  if ((ctx.txnType ?? TXN_TYPE.FULL) === TXN_TYPE.NC) return !!(ctx.closeReason ?? null)?.reason;
  if (legs.length === 0) return false;
  // returns settle negative; the rules below do not apply to them
  if (ctx.returnSale) return !selectOverRefund(legs, ctx);
  if (!totals) return true;

  /**
   * v1 runs a DIFFERENT PAIR of validators per transaction type
   * (`handleFullPaymentTranscation:398`), and neither omission is an accident:
   *
   *   FULL    validatePayments + checkValidAmount
   *   PARTIAL validatePayments only  — skipping "must cover the bill" is exactly
   *                                   what lets a partial payment through
   *   SPLIT   checkValidAmount only  — the per-leg checks are skipped
   */
  if ((ctx.txnType ?? TXN_TYPE.FULL) !== TXN_TYPE.SPLIT && !selectActiveLegValid(legs, opts?.activeLegId ?? null, totals)) return false;
  if ((ctx.txnType ?? TXN_TYPE.FULL) !== TXN_TYPE.PARTIAL && !selectCoversBill(totals)) return false;

  /**
   * And then, for EVERY type including PARTIAL, v1 gates closing with a balance on
   * the operator's permission (`handleCheckoutAction:456`). Confirm always takes
   * the close path — `handleCheckout` IS `handleCheckoutAction` (`:356`) — so a
   * partial payment closes the sale with an Amount Due, and needs the right to
   * leave one behind.
   */
  if (totals.balance > 0 && opts?.allowOpenBalance !== true) return false;

  return true;
}

/**
 * Permissions the host holds; the core never reads `featureRestrictions` itself.
 */
export interface TxnTypePermissions {
  allowSaveOverride?:boolean;
  /** CLOSE_SALE_BALANCE — gates PARTIAL being OFFERED, and closing with a balance */
  allowOpenBalance?: boolean;
  /** NON_CHARGEABLE_SALE */
  allowNonChargeable?: boolean;
  /** SPLIT_PAYMENTS */
  allowSplit?: boolean;
}

/**
 * Which transaction types the operator may switch to, mirroring the four cases in
 * `CheckoutHeader.paymentMethodShortcuts:87` and its guards:
 *
 *   FULL     unless `hideFullPayment`
 *   NC       needs NON_CHARGEABLE_SALE
 *   PARTIAL  needs `showPartialPayment()` = CLOSE_SALE_BALANCE && !returnSale,
 *            and is also hidden by `hideFullPayment`
 *   SPLIT    needs SPLIT_PAYMENTS
 *
 * and, above all of them, `allowOnlySplit()` (`:138`): once the SALE already carries
 * persisted payments and the session has legs, SPLIT is the only option left.
 */
export function selectAvailableTxnTypes(
  legs: PaymentLeg[],
  ctx: SaleContext,
  perms?: TxnTypePermissions
): TxnType[] {
  const persisted = (ctx.sale?.paymentInfo?.payments ?? []).length > 0;
  if (persisted && legs.length > 0) return [TXN_TYPE.SPLIT];

  // hideFullPayment (`paymenthelper:754`): an external leg that has been to a
  // provider takes FULL — and with it PARTIAL — off the table
  const hideFull = legs.some((leg) => leg.type === "External" && !!leg.paymentStatus);

  const available: TxnType[] = [];
  if (!hideFull) available.push(TXN_TYPE.FULL);
  // never on a return or an already-CLOSED sale: v1's tab rule (`featureRestrictionHelper:218`).
  // The shortcuts read this same list, so Ctrl+2 obeys it too — v1's does not (§36 R1)
  if (perms?.allowNonChargeable && !ctx.returnSale && ctx.sale?.saleStatus !== "Closed") available.push(TXN_TYPE.NC);
  if (perms?.allowOpenBalance && !ctx.returnSale && !hideFull) available.push(TXN_TYPE.PARTIAL);
  if (perms?.allowSplit) available.push(TXN_TYPE.SPLIT);
  return available;
}

/**
 * Switching type with legs already entered DISCARDS them — v1 pops a confirm and
 * then `resetDataSwitchPayment` (`CheckoutHeader.handleConfirmClick:147`). The host
 * asks; this says whether asking is needed.
 */
export function selectSwitchDiscardsLegs(legs: PaymentLeg[], ctx: SaleContext): boolean {
  // v1's condition verbatim (`CheckoutHeader.handleChange:117`):
  // `payments.size > 0 || checkoutModalStore?.saleStatus?.reason` — an NC reason
  // is entered work too, and `resetDataSwitchPayment` clears it.
  return legs.length > 0 || !!(ctx.closeReason ?? null)?.reason;
}


/**
 * Non-cash tender may not exceed what is still owed.
 *
 * Cash is exempt: overpaying in cash is how change happens. A posted leg is
 * exempt too — the provider already decided that amount, and clamping it would
 * disagree with money we hold.
 *
 * Mirrors v1's live rule (`paymenthelper.validateAmount`, and the clamp in
 * `TouchInput.validateAmount`), including the part that is easy to miss: the
 * leg's OWN amount is added back before comparing, because it is already counted
 * in the outstanding balance. Without that, editing a leg down and back up again
 * would cap it at the wrong number.
 *
 * v1's sibling rule — "payment cannot be LESS than balance for non-cash" — stays
 * off. It has been unreachable for as long as the inert slice existed, and
 * under-tendering a non-cash leg is a legitimate partial payment.
 *
 * Returns the capped amount, sign preserved, so the caller can detect a cap by
 * comparing and tell the operator why.
 */
export function capNonCashAmount(
  legs: PaymentLeg[],
  paymentId: string,
  amount: number,
  totals: PaymentTotals,
  /**
   * The MODE's `isCash`. A re-price is often a mode SWITCH, and the leg carries
   * the OLD mode's flag until it returns — reading it there lets a cash -> card
   * switch keep its over-tender (§9, and why `priceLeg` takes the mode).
   */
  isCash?: boolean
): number {
  const leg = legs.find((candidate) => candidate.paymentId === paymentId);
  if ((isCash ?? leg?.isCash) || leg?.posted) return amount;

  // no leg yet — `pick` prices before it creates one — is nothing to add back
  const available = availableForLeg(totals, leg);
  if (Math.abs(amount) <= available) return amount;
  return amount < 0 ? -available : available;
}

/**
 * Can the operator DELETE this leg? Mirrors v1's `isSimpleDeletable`
 * (`SplitComponentDesktop:56`): `!paymentStatus && mode !== 'Coupon' && type !== External`.
 *
 * The rule is about provenance, not tidiness — a leg with a `paymentStatus` has
 * been through a provider, and a coupon was granted rather than tendered. Neither
 * is the operator's to discard from the bill.
 */
export function selectCanRemoveLeg(legs: PaymentLeg[], paymentId: string): boolean {
  const leg = legs.find((candidate) => candidate.paymentId === paymentId);
  if (!leg) return false;
  /**
   * The settling guard that used to sit here is gone, not lost: the ENGINE
   * refuses every edit while a provider holds a leg — `canEdit` on
   * `upsertLeg`/`patchLeg`/`removeLeg` (§12) — so asking the money layer to
   * re-answer it meant threading a session status into a rule that is about
   * PROVENANCE. What remains is v1's `isSimpleDeletable` and nothing else.
   */
  if (leg.type === LEG_TYPE_COUPON) return false;
  if (leg.type === "External") return false;
  return !leg.paymentStatus;
}

/**
 * Can the OPERATOR close the session?
 *
 * The reducer's `closeSession` is deliberately unguarded, because it is also the
 * UNMOUNT teardown and that must always succeed — the same two-callers split as
 * `removeLeg` / `selectCanRemoveLeg`, so the affordance rule lives here. Without
 * it the machine's "no CLOSE while SETTLING" was documented but nowhere enforced.
 */
export function selectCanClose(
  // unused, but positional: kept in step with `selectCloseBlockedReason`
  _legs: PaymentLeg[],
  _ctx: SaleContext,
  totals?: PaymentTotals,
  opts?: { allowOpenBalance?: boolean; settling?: boolean; activeLegId?: string | null }
): boolean {
  if (opts?.settling) return false;
  /**
   * v1's SECOND close guard, which v2 did not have
   * (`handleCheckoutClose:553`):
   *
   *   !allowedFeatures().includes(CLOSE_SALE_BALANCE) && !checkValidAmount(tendered)
   *
   * Walking away from a bill that is not covered is a PERMISSION, not a
   * convenience — without `CLOSE_SALE_BALANCE` the operator may not abandon a
   * checkout mid-tender, and v1 refuses the cross, the backdrop and Escape alike.
   * `checkValidAmount` is `selectCoversBill`, sub-₹1 tolerance included, and it
   * exempts returns for the same reason `selectCoversBill` does.
   *
   * `totals` is optional so the teardown path — which is past the point where the
   * operator's rule applies — can ask the cheaper question.
   */
  /**
   * A FLAGGED total is not a covered bill. When the library rejects the sale
   * `balance` is 0 and `selectCoversBill` passes, so the cross would let the
   * operator walk away from a bill that was never calculated. `selectCanCommit`
   * already refuses on `error` (:333) and both builders throw on it; this is the
   * third door. v1 cannot reach the state at all — `checkValidAmount:431` reads
   * `saleData.paymentInfo.balanceAmount` off the record and never calculates.
   */
  if (totals && opts?.allowOpenBalance !== true && (totals.error || !selectCoversBill(totals))) {
    return false;
  }
  return true;
}

/** Why `selectCanClose` said no, for the message the host shows. */
export function selectCloseBlockedReason(
  legs: PaymentLeg[],
  ctx: SaleContext,
  totals?: PaymentTotals,
  opts?: { allowOpenBalance?: boolean; settling?: boolean; activeLegId?: string | null }
): string | null {
  if (selectCanClose(legs, ctx, totals, opts)) return null;
  if (opts?.settling) return "Can't close. Payment in progress";
  // v1 pops `UI_MSG_AMOUNT_LESS_THAN_BALANCE` from inside `checkValidAmount`
  if (totals?.error && opts?.allowOpenBalance !== true) return "Can't close. The bill has not calculated.";
  if (totals && opts?.allowOpenBalance !== true && !selectCoversBill(totals)) {
    return "Amount should be greater than or equal to the balance amount.";
  }
  return "Can't close right now";
}



/**
 * Rounding of an amount the cashier is typing INTO a specific leg — a different
 * question from `roundBill`, and the only case where naming a mode is correct,
 * because the leg being edited is the subject. Replaces TouchInput's
 * `roundOffBillAmount(paymentModeMap[mode], …)` plus the
 * `enableRoundingCashOnly && roundingMode !== DO_NOT_ROUND` condition it wraps
 * that call in — the library already applies both.
 */
export function roundLegAmount(
  leg: Pick<PaymentLeg, "isCash">,
  amount: number | string,
  params: any
): number {
  return parseFloat(
    saleutil.roundOffBillAmountBasedOnPaymentModes(leg, params, amount, undefined) as any
  );
}

/** Snapshot + the normalized legs + tip. Shared by the two things that leave the session. */
function assembleSale(legs: PaymentLeg[], ctx: SaleContext, totals: PaymentTotals): any {
  const sale = structuredClone(totals.sale);
  const { paymentsArr } = normalizeLegs(legs, !!ctx.returnSale);
  sale.paymentInfo = { ...(sale.paymentInfo ?? {}), payments: paymentsArr, tip: (ctx.tip ?? 0) };
  return sale;
}

/**
 * The sale to POST when REQUESTING an external payment (docs §10).
 *
 * Same assembly as `buildCommit`, deliberately without the derived change leg and
 * without the balance clamp: this is a request, not a settlement. The backend
 * needs a saved sale to hang the provider transaction off, which is why asking
 * for an external payment is the one write the operator does not trigger.
 */
export function buildRequestSale(legs: PaymentLeg[], ctx: SaleContext, params: any): any {
  const totals = selectTotals(legs, ctx, params);
  if (totals.error) {
    const err: any = new Error(`[payments] cannot request payment: ${totals.error}`);
    err.code = totals.error;
    throw err;
  }
  return assembleSale(legs, ctx, totals);
}

export type CommitIntent = "save" | "close";

export interface Commit {
  intent: CommitIntent;
  /** the sale to persist — the ONLY thing that leaves the session */
  sale: any;
  totals: PaymentTotals;
}

/**
 * Turns the draft into the sale to persist. This is the single write boundary:
 * until the host dispatches the result, the real sale is untouched.
 *
 * The cash change leg is DERIVED here rather than stored, so re-entering an
 * amount can't leave a stale change leg behind (a v1 failure mode).
 */
export function buildCommit(
  legs: PaymentLeg[],
  ctx: SaleContext,
  params: any,
  intent: CommitIntent
): Commit {
  const totals = selectTotals(legs, ctx, params);
  if (totals.error) {
    const err: any = new Error(`[payments] cannot commit: ${totals.error}`);
    err.code = totals.error;
    throw err;
  }
  if (selectOverRefund(legs, ctx)) {
    const err: any = new Error("[payments] cannot refund more than the sale owes");
    err.code = "RETURN_PAYMENT_ERROR";
    throw err;
  }
  const sale = assembleSale(legs, ctx, totals);

  if ((ctx.txnType ?? TXN_TYPE.FULL) === TXN_TYPE.NC) {
    sale.statusInfo = ctx.closeReason ?? {};
    return { intent, sale, totals };
  }

  if (totals.change > 0 && !ctx.returnSale) {
    sale.paymentInfo.payments = [
      ...(sale.paymentInfo?.payments ?? []),
      { mode: "Cash", amount: -totals.change, isCash: true },
    ];
  }
  // v1 clamps on both paths (`balance > 0 ? balance : 0`). For a sale the balance
  // is already >= 0; for a return it is signed, and the persisted field is not.
  sale.paymentInfo.balanceAmount = totals.balance > 0 ? totals.balance : 0;

  return { intent, sale, totals };
}
